import { Callback } from "game-common/util"
import { Graphics } from "pixi.js"
import { GuiManager } from "../gui_manager"
import { DialogOptions } from "./dialog"
import { Modal } from "./modal"
import { disposeTextTextures } from "../text/dispose_text"

export interface FullScreenModalProps extends Pick<DialogOptions, "dragHandle" | "dragBounds" | "onDrag"> {
    padding?: number
    opaque?: boolean
    unescapable?: boolean
    // Override the full-screen dark backdrop alpha. Pass 0 for a non-blocking modal that lets the
    // world stay visible behind it (e.g. a docked side panel that only draws its own opaque region).
    backgroundAlpha?: number
    // Top-right corner (content coordinates) to hang the close button off -- see DialogOptions. A
    // modal that only paints part of the full-screen rect should pass its own panel's corner.
    closeAnchor?: DialogOptions["closeAnchor"]
    closeButtonScale?: number
}

export abstract class FullScreenModal {
    protected guiManager: GuiManager
    private show: boolean
    modal: Modal
    private surface: Graphics
    private padding: number
    onClose: Callback<boolean>
    private opaque: boolean
    private unescapable: boolean
    private backgroundAlpha?: number
    private closeAnchor?: DialogOptions["closeAnchor"]
    private closeButtonScale?: number
    private dragOptions: Pick<DialogOptions, "dragHandle" | "dragBounds" | "onDrag">

    constructor(guiManager: GuiManager, props?: FullScreenModalProps) {
        this.guiManager = guiManager
        this.show = false
        this.surface = new Graphics()
        this.padding = props?.padding ?? 10
        this.opaque = props?.opaque || false
        this.unescapable = props?.unescapable || false
        this.backgroundAlpha = props?.backgroundAlpha
        this.closeAnchor = props?.closeAnchor
        this.closeButtonScale = props?.closeButtonScale
        this.dragOptions = { dragHandle: props?.dragHandle, dragBounds: props?.dragBounds, onDrag: props?.onDrag }
    }

    abstract createContent(): Graphics

    isOpen = () => this.show
    update = () => {
        this.rebuildSurface()
    }

    // Repaint the content. Split out from update() so a subclass can decide for itself WHEN to
    // repaint (the character builder is driven by player-stat updates it mostly wants to ignore)
    // and still get the same teardown.
    //
    // Frees the previous content's per-instance Text textures before rebuilding -- otherwise every
    // open of this modal (inventory, character sheet, etc.) strands all its grid item labels /
    // quantity / stat Text textures. Shared item-sprite textures are left intact.
    protected rebuildSurface = () => {
        ;[...this.surface.children].forEach(disposeTextTextures)
        this.surface.removeChildren()
        this.surface.addChild(this.createContent())
    }

    width = () => this.modal?.width
    height = () => this.modal?.height

    close = () => {
        if (!this.show) {
            return
        }
        this.show = false
        this.modal?.close()
    }

    toggle = (onClose?: Callback<boolean>) => {
        if (this.show) {
            this.close()
            return
        }
        this.show = true
        this.modal = this.guiManager.createModal({
            ...this.dragOptions,
            padding: this.padding,
            content: this.surface,
            fullScreen: true,
            backgroundAlpha: this.backgroundAlpha !== undefined ? this.backgroundAlpha : !this.opaque ? 0.91 : 1.0,
            plain: true,
            unescapeable: this.unescapable,
            closeAnchor: this.closeAnchor,
            closeButtonScale: this.closeButtonScale,
            onResize: () => this.update(),
            onClosed: () => { this.show = false },
            closeCallback: (clicked: boolean) => {
                if (this.onClose) {
                    this.onClose(clicked)
                } else if (onClose) {
                    onClose(clicked)
                }
            },
        })

        this.update()
    }
}
